import { useCallback, useEffect, useMemo, useState } from 'react';
import { getKnowledgeItemType, getModuleKey } from '../productKnowledgeUtils';

const useProductKnowledgeTableState = ({ staged, approved, deleted }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeTab, setActiveTab] = useState('staged');
  const [expandedParents, setExpandedParents] = useState(() => new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    source: '',
    knowledgeType: '',
    module: '',
  });
  const [hierarchyView, setHierarchyView] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const getCurrentItems = useCallback(() => {
    switch (activeTab) {
      case 'staged':
        return staged;
      case 'approved':
        return approved;
      case 'deleted':
        return deleted;
      default:
        return [];
    }
  }, [activeTab, approved, deleted, staged]);

  const isSubtaskItem = useCallback((item) => {
    const issueType = item?.content?.metadata?.issue_type;
    if (!issueType) return false;
    const normalized = issueType.toLowerCase().replace(/-/g, ' ').replace(/\s+/g, ' ');
    return normalized.includes('subtask') || normalized.includes('sub task');
  }, []);

  const subtasksByParentKey = useMemo(() => {
    const items = getCurrentItems();
    const map = {};
    for (const item of items) {
      if (!isSubtaskItem(item)) continue;
      const parentKey = item?.content?.metadata?.parent_key;
      if (!parentKey) continue;
      if (!map[parentKey]) map[parentKey] = [];
      map[parentKey].push(item);
    }
    return map;
  }, [getCurrentItems, isSubtaskItem]);

  const filterOptions = useMemo(() => {
    const items = getCurrentItems();
    const sources = [...new Set(items.map((item) => item.source))].filter(Boolean).sort();
    const knowledgeTypes = [...new Set(items.map((item) => getKnowledgeItemType(item)))].filter(Boolean).sort();
    const modules = [...new Set(
      items.flatMap((item) => (item.modules || []).map((moduleValue) => getModuleKey(moduleValue)).filter(Boolean))
    )].sort();

    return { sources, knowledgeTypes, modules };
  }, [getCurrentItems]);

  const applySearchAndFacets = useCallback((items) => {
    return items.filter((item) => {
      const title = item.content?.name || item.content?.title || 'Untitled';
      const description = item.content?.description || '';
      const matchesSearch =
        title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesSource = !filters.source || item.source === filters.source;
      const matchesKnowledgeType = !filters.knowledgeType || getKnowledgeItemType(item) === filters.knowledgeType;
      const matchesModule = !filters.module || (() => {
        const moduleKeys = (item.modules || []).map((moduleValue) => getModuleKey(moduleValue)).filter(Boolean);
        return moduleKeys.includes(filters.module);
      })();

      return matchesSearch && matchesSource && matchesKnowledgeType && matchesModule;
    });
  }, [filters, searchTerm]);

  const applyHierarchy = useCallback((items) => {
    return items.filter((item) => {
      const isSubtask = isSubtaskItem(item);
      if (hierarchyView === 'top_level') return !isSubtask;
      if (hierarchyView === 'subtasks') return isSubtask;
      return true;
    });
  }, [hierarchyView, isSubtaskItem]);

  const baseFilteredItems = useMemo(
    () => applySearchAndFacets(getCurrentItems()),
    [applySearchAndFacets, getCurrentItems]
  );

  const filteredItems = useMemo(
    () => applyHierarchy(baseFilteredItems),
    [applyHierarchy, baseFilteredItems]
  );

  const stagedTabCount = useMemo(
    () => applyHierarchy(applySearchAndFacets(staged)).length,
    [applyHierarchy, applySearchAndFacets, staged]
  );
  const approvedTabCount = useMemo(
    () => applyHierarchy(applySearchAndFacets(approved)).length,
    [applyHierarchy, applySearchAndFacets, approved]
  );
  const deletedTabCount = useMemo(
    () => applyHierarchy(applySearchAndFacets(deleted)).length,
    [applyHierarchy, applySearchAndFacets, deleted]
  );

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const aValue = sortConfig.key === 'title'
        ? a.content?.name || a.content?.title || ''
        : a[sortConfig.key] || '';
      const bValue = sortConfig.key === 'title'
        ? b.content?.name || b.content?.title || ''
        : b[sortConfig.key] || '';

      if (sortConfig.direction === 'asc') return aValue.localeCompare(bValue);
      return bValue.localeCompare(aValue);
    });
  }, [filteredItems, sortConfig]);

  const totalPages = Math.ceil(sortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = sortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleSelectAll = useCallback((checked) => {
    if (checked) {
      setSelectedIds(paginatedItems.map((item) => item.knowledge_id));
    } else {
      setSelectedIds([]);
    }
  }, [paginatedItems]);

  const handleSelectItem = useCallback((knowledgeId, checked) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, knowledgeId]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => id !== knowledgeId));
    }
  }, []);

  const handleDeleteSelected = useCallback((onConfirm) => {
    if (typeof onConfirm === 'function') {
      onConfirm(selectedIds);
    }
  }, [selectedIds]);

  const handleToggleParentExpanded = useCallback((jiraKey) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(jiraKey)) next.delete(jiraKey);
      else next.add(jiraKey);
      return next;
    });
  }, []);

  const emptyStateMessage = useMemo(() => {
    const raw = getCurrentItems();
    const afterFacet = applySearchAndFacets(raw);
    const hiddenByTopLevel =
      raw.length > 0 &&
      afterFacet.length > 0 &&
      hierarchyView === 'top_level' &&
      applyHierarchy(afterFacet).length === 0;
    const hiddenBySubtasksOnly =
      raw.length > 0 &&
      afterFacet.length > 0 &&
      hierarchyView === 'subtasks' &&
      applyHierarchy(afterFacet).length === 0;

    if (searchTerm || Object.values(filters).some((value) => value)) {
      return 'No items match your search or filter criteria.';
    }
    if (hiddenByTopLevel) {
      return `${afterFacet.length} item(s) are hidden while viewing "Top-level only" (e.g. JIRA Sub-tasks). Open Filters → Hierarchy → "All (Top-level + Subtasks)".`;
    }
    if (hiddenBySubtasksOnly) {
      return `${afterFacet.length} item(s) are hidden: none are Sub-tasks. Switch Hierarchy to "All" or "Top-level only".`;
    }
    if (raw.length === 0) {
      return `No ${activeTab} knowledge items found.`;
    }
    return 'No items match your current view.';
  }, [activeTab, applyHierarchy, applySearchAndFacets, filters, getCurrentItems, hierarchyView, searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, activeTab, hierarchyView, searchTerm]);

  useEffect(() => {
    setExpandedParents(new Set());
  }, [activeTab, hierarchyView, filters, searchTerm]);

  return {
    selectedIds,
    setSelectedIds,
    activeTab,
    setActiveTab,
    expandedParents,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortConfig,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    hierarchyView,
    setHierarchyView,
    showFilters,
    setShowFilters,
    getCurrentItems,
    isSubtaskItem,
    subtasksByParentKey,
    filterOptions,
    applySearchAndFacets,
    applyHierarchy,
    baseFilteredItems,
    sortedItems,
    totalPages,
    startIndex,
    paginatedItems,
    stagedTabCount,
    approvedTabCount,
    deletedTabCount,
    handleSort,
    handleSelectAll,
    handleSelectItem,
    handleDeleteSelected,
    handleToggleParentExpanded,
    emptyStateMessage,
  };
};

export default useProductKnowledgeTableState;
